import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API_BASE_URL =
  "https://dev.oliveeb.com/olitask-service/api/v1/donation-credit-purposes";

export default function useDonationsPurposes() {
  const authToken = sessionStorage.getItem("authToken");
  const [donationPurposes, setDonationPurposes] = useState([]);
  const [selectedPurpose, setSelectedPurpose] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // handle axios errors
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    setError(message);
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  // fetch all donation credit purposes
  const fetchDonationPurposes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const firstResponse = await axios.get(
        `${API_BASE_URL}?page=0&size=${50}`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      const { content: firstContent = [], totalPages = 1 } = firstResponse.data;
      let allPurposes = [...firstContent];

      if (totalPages > 1) {
        const promises = [];
        for (let p = 1; p < totalPages; p++) {
          promises.push(
            axios.get(`${API_BASE_URL}?page=${p}&size=${50}`, {
              headers: { Authorization: `Bearer ${authToken}` },
            })
          );
        }

        const responses = await Promise.all(promises);
        responses.forEach((res) => {
          allPurposes = allPurposes.concat(res.data.content || []);
        });
      }
      setDonationPurposes(allPurposes);
    } catch (err) {
      handleAxiosErrors(err, "Fetch credits");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // Fetch donation purpose by ID
  const fetchDonationPurposeById = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_BASE_URL}/${id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setSelectedPurpose(res.data);
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "fetch donation purpose details");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // Add a new donation purpose
  const addDonationPurpose = useCallback(
    async (newPurposeData) => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.post(API_BASE_URL, newPurposeData, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        });

        // Update local state to include the new purpose
        setDonationPurposes((prev) => [res.data, ...prev]);

        toast.success("Donation Credit Purpose added successfully!", {
          theme: "colored",
        });

        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "add donation credit purpose");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // Update donation purpose
  const updateDonationPurpose = useCallback(
    async (id, updatedData) => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.put(`${API_BASE_URL}/${id}`, updatedData, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            // "Content-Type": "application/json",
          },
        });

        // Update local state
        setDonationPurposes((prev) =>
          prev.map((purpose) => (purpose.id === id ? res.data : purpose))
        );

        // If currently selected key matches, update it
        setSelectedPurpose((prev) =>
          prev && prev.id === id ? res.data : prev
        );

        toast.success("Donation Credit purpose updated successfully!", {
          theme: "colored",
        });
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "update donation credit purpose");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // Delete donation purpose
  const deleteDonationPurpose = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        await axios.delete(`${API_BASE_URL}/${id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        // Remove deleted purpose from local state
        setDonationPurposes((prev) => prev.filter((key) => key.id !== id));
        toast.success("Donation Credit Purpose deleted successfully!", {
          theme: "colored",
        });
      } catch (err) {
        handleAxiosErrors(err, "Delete donation credit purpose");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // fetch automatically on mount
  useEffect(() => {
    if (authToken) fetchDonationPurposes();
  }, [authToken, fetchDonationPurposes]);

  return {
    donationPurposes,
    selectedPurpose,
    loading,
    error,
    fetchDonationPurposes,
    fetchDonationPurposeById,
    addDonationPurpose,
    updateDonationPurpose,
    deleteDonationPurpose,
    setSelectedPurpose,
  };
}
